import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  CheckSquare,
  ChevronLeft,
  Gift,
  Loader2,
  Search,
  Square,
  User,
} from "lucide-react";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import WalletTabs from "@/components/rewards/WalletTabs";
import { sharedApi, SHARED_ORIGIN } from "@/lib/api";

/**
 * Iter-619 — Buy AllSale points (self-use or gift) — `/points/buy`.
 *
 * Tiered face values (+$2 fee), recipient = me | gift (existing verified
 * account, looked up by registered mobile number), Stripe hosted checkout;
 * points land in the NZ partner-store wallet with a 3-year expiry
 * (NZ gift-card law). Redemption is unchanged — same wallet the till uses.
 *   GET  /points/purchase/options
 *   POST /points/purchase/lookup-recipient {query}
 *   POST /points/purchase/checkout {face_nzd, recipient_id?, message?, accept_terms, return_origin}
 *   GET  /points/purchase/status/{purchase_id}   (3s poll after Stripe return)
 *   GET  /points/purchase/mine
 */

const BRAND = "#0F70A8"; // NZ store wallet colour (matches the Rewards balance card)

function errDetail(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (typeof d?.message === "string" && d.message.trim()) return d.message;
  return fallback;
}

export default function BuyPointsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [opts, setOpts] = useState(null);
  const [tier, setTier] = useState(20);
  const [mode, setMode] = useState("self"); // "self" | "gift"
  const [query, setQuery] = useState("");
  const [recipient, setRecipient] = useState(null); // {recipient_id, display_name} | null
  const [message, setMessage] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(""); // "" | "lookup" | "pay"
  const [pending, setPending] = useState(null); // purchase id awaiting Stripe
  const [done, setDone] = useState(null); // {points, is_gift, to, balance} | null
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [o, h] = await Promise.all([
        sharedApi.get("/points/purchase/options"),
        sharedApi.get("/points/purchase/mine"),
      ]);
      setOpts(o.data);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(errDetail(err, "Could not load options"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Stripe returns to /points/buy?paid=1&pid=… (cancelled=1 on abort).
  useEffect(() => {
    if (params.get("paid") === "1" && params.get("pid")) setPending(params.get("pid"));
    if (params.get("cancelled") === "1")
      toast.info("Purchase cancelled — no charge was made");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll the purchase until Stripe confirms (webhook usually beats us).
  useEffect(() => {
    if (!pending) return undefined;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const { data: r } = await sharedApi.get(`/points/purchase/status/${pending}`);
        if (r.payment_status === "paid") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          const p = r.purchase;
          setDone({ points: p.points, is_gift: p.is_gift, to: p.recipient_display, balance: r.nz_balance });
          setPending(null);
          setAgree(false);
          load();
        } else if (r.payment_status === "failed" || tries > 200) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setPending(null);
          if (r.payment_status === "failed")
            toast.info("Payment didn't complete — no points were added");
        }
      } catch {
        // transient — keep polling
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = async () => {
    if (query.trim().length < 3) {
      toast.info("Enter the recipient's mobile number");
      return;
    }
    setBusy("lookup");
    try {
      const { data: r } = await sharedApi.post("/points/purchase/lookup-recipient", {
        query: query.trim(),
      });
      setRecipient(r);
    } catch (err) {
      setRecipient(null);
      toast.error(errDetail(err, "Lookup failed"));
    } finally {
      setBusy("");
    }
  };

  const pay = async () => {
    if (!opts) return;
    if (mode === "gift" && !recipient) {
      toast.info("Find the gift recipient first");
      return;
    }
    if (!agree) {
      toast.info("Please accept the points terms to continue");
      return;
    }
    setBusy("pay");
    setDone(null);
    try {
      const { data: r } = await sharedApi.post("/points/purchase/checkout", {
        face_nzd: tier,
        recipient_id: mode === "gift" ? recipient?.recipient_id : undefined,
        message: mode === "gift" ? message.trim() || undefined : undefined,
        accept_terms: true,
        return_origin: window.location.origin,
      });
      if (r?.url) {
        setPending(r.purchase_id);
        window.location.assign(r.url);
        return; // navigating away — keep the pay spinner on
      }
      setBusy("");
    } catch (err) {
      toast.error(errDetail(err, "Checkout failed"));
      setBusy("");
    }
  };

  const selected = opts?.tiers?.find((t) => t.face_nzd === tier) || null;
  const remaining = opts?.daily_remaining_nzd ?? 0;
  const disabled = busy === "pay" || !!pending || !agree || (mode === "gift" && !recipient);

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo
        title="Buy AllSale points"
        description="Buy AllSale points for yourself or send them as a gift — redeem at partner stores across New Zealand."
      />
      <Breadcrumbs items={[{ label: "Rewards", to: "/rewards" }, { label: "Buy points" }]} />

      <div className="mt-6 mb-4 flex items-center gap-3">
        <button
          type="button"
          data-testid="bp-back"
          onClick={() => navigate(-1)}
          className="w-10 h-10 grid place-items-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5 text-slate-700" />
        </button>
        {/* Iter 629 — this screen is the "Buy points" tab of My Wallet. */}
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">My Wallet</h1>
      </div>
      <div className="mb-6">
        <WalletTabs active="buy" />
      </div>

      {!opts ? (
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-10 grid place-items-center" data-testid="bp-loading">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-5">
          {done && (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-center" data-testid="bp-done">
              <CheckCircle2 className="w-9 h-9 mx-auto text-emerald-600" />
              <div className="mt-2 font-display text-xl font-black text-slate-900">
                {done.is_gift ? `Gift sent to ${done.to || "your friend"} 🎁` : "Points added 🎉"}
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {done.is_gift
                  ? `${done.points.toLocaleString()} pts are already in their account.`
                  : `+${done.points.toLocaleString()} pts · your NZ store balance is now ${Number(done.balance ?? 0).toLocaleString()} pts.`}
              </p>
              <Link
                to="/rewards"
                data-testid="bp-done-rewards"
                className="mt-3 inline-flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-6 py-2.5 transition-colors"
              >
                Back to Rewards
              </Link>
            </div>
          )}

          {pending && (
            <div className="rounded-2xl bg-orange-50 border border-orange-200 p-5 text-center space-y-2" data-testid="bp-pending">
              <Loader2 className="w-5 h-5 mx-auto animate-spin" style={{ color: BRAND }} />
              <p className="text-sm text-slate-700">
                Waiting for payment… finish in the Stripe window and we&apos;ll add the points automatically.
              </p>
              <button
                type="button"
                data-testid="bp-pending-cancel"
                onClick={() => setPending(null)}
                className="text-xs text-slate-500 underline"
              >
                I didn&apos;t pay — cancel
              </button>
            </div>
          )}

          <div className="rounded-xl bg-blue-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold" style={{ color: BRAND }}>💳 Purchased / gifted points</span>
              <span className="text-lg font-black" style={{ color: BRAND }} data-testid="bp-balance">
                {Number(opts.nz_purchased_balance ?? 0).toLocaleString()} pts
              </span>
            </div>
            {Number(opts.nz_purchased_balance) > 0 && opts.nz_purchased_next_expiry_at && (
              <p className="mt-0.5 text-xs text-slate-500" data-testid="bp-balance-expiry">
                valid until{" "}
                {new Date(opts.nz_purchased_next_expiry_at).toLocaleDateString("en-NZ", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}{" "}
                (3 years)
              </p>
            )}
          </div>

          <div>
            <div className="text-sm font-extrabold text-slate-900 mb-2">1 · Choose an amount</div>
            <div className="grid grid-cols-2 gap-2.5">
              {(opts.tiers || []).map((t) => {
                const on = t.face_nzd === tier;
                const blocked = t.face_nzd > remaining;
                return (
                  <button
                    key={t.face_nzd}
                    type="button"
                    disabled={blocked}
                    onClick={() => setTier(t.face_nzd)}
                    data-testid={`bp-tier-${t.face_nzd}`}
                    className={`rounded-2xl border-2 p-4 text-center transition-colors min-h-[96px] ${
                      blocked ? "opacity-40 cursor-not-allowed border-slate-200 bg-white" : ""
                    } ${on ? "text-white" : "bg-white border-slate-200 hover:border-slate-300"}`}
                    style={on ? { backgroundColor: BRAND, borderColor: BRAND } : undefined}
                  >
                    <div className={`text-2xl font-black ${on ? "text-white" : "text-slate-900"}`}>
                      ${t.face_nzd}
                    </div>
                    <div className={`text-xs mt-0.5 ${on ? "text-blue-100" : "text-slate-500"}`}>
                      {t.points.toLocaleString()} pts
                    </div>
                    <div className={`text-sm font-extrabold mt-1.5 ${on ? "text-white" : "text-slate-900"}`}>
                      Pay ${t.pay_nzd.toFixed(0)}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-slate-500 leading-relaxed">
              Includes a ${Number(opts.fee_nzd ?? 2).toFixed(0)} service fee · 100 pts = $1 · daily
              limit ${opts.daily_cap_nzd} in points
              {remaining < opts.daily_cap_nzd ? ` (you can still buy $${remaining.toFixed(0)} today)` : ""}
            </p>
          </div>

          <div>
            <div className="text-sm font-extrabold text-slate-900 mb-2">2 · Who is it for?</div>
            <div className="flex rounded-xl bg-slate-100 p-1 gap-1">
              <button
                type="button"
                data-testid="bp-mode-self"
                onClick={() => setMode("self")}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg min-h-[44px] text-sm font-extrabold transition-colors ${
                  mode === "self" ? "text-white" : "text-slate-700 hover:bg-white/60"
                }`}
                style={mode === "self" ? { backgroundColor: BRAND } : undefined}
              >
                <User className="w-4 h-4" /> For myself
              </button>
              <button
                type="button"
                data-testid="bp-mode-gift"
                onClick={() => setMode("gift")}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg min-h-[44px] text-sm font-extrabold transition-colors ${
                  mode === "gift" ? "text-white" : "text-slate-700 hover:bg-white/60"
                }`}
                style={mode === "gift" ? { backgroundColor: BRAND } : undefined}
              >
                <Gift className="w-4 h-4" /> As a gift
              </button>
            </div>

            {mode === "gift" && (
              <div className="mt-3 rounded-2xl bg-white border border-slate-200 p-4">
                {recipient ? (
                  <div
                    className="flex items-center justify-between rounded-xl bg-emerald-50 px-3 py-2.5"
                    data-testid="bp-recipient"
                  >
                    <span className="text-sm text-slate-800">
                      🎁 Sending to <span className="font-black">{recipient.display_name}</span>
                    </span>
                    <button
                      type="button"
                      data-testid="bp-recipient-change"
                      onClick={() => {
                        setRecipient(null);
                        setQuery("");
                      }}
                      className="text-sm font-bold"
                      style={{ color: BRAND }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      className="flex-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-500"
                      placeholder="Recipient's mobile number, e.g. 021 234 5678"
                      autoComplete="tel"
                      inputMode="tel"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          lookup();
                        }
                      }}
                      data-testid="bp-recipient-query"
                    />
                    <button
                      type="button"
                      data-testid="bp-recipient-find"
                      onClick={lookup}
                      disabled={busy === "lookup"}
                      className="w-11 h-11 rounded-xl grid place-items-center text-white disabled:opacity-60"
                      style={{ backgroundColor: BRAND }}
                      aria-label="Find recipient"
                    >
                      {busy === "lookup" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                )}
                <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                  Enter the mobile number they registered with on AllSale. Only existing accounts can
                  receive gifts — points land in their account instantly.
                </p>
                <input
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-500"
                  placeholder="Add a short message (optional)"
                  value={message}
                  maxLength={100}
                  onChange={(e) => setMessage(e.target.value.slice(0, 100))}
                  data-testid="bp-gift-message"
                />
                <p className="mt-1 text-right text-xs text-slate-400">{message.length}/100</p>
              </div>
            )}
          </div>

          <div>
            <div className="text-sm font-extrabold text-slate-900 mb-2">3 · Terms</div>
            <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid="bp-terms">
              {(opts.terms || []).map((t, i) => (
                <p key={i} className="text-xs text-slate-500 leading-relaxed mb-1">
                  • {t}
                </p>
              ))}
              <button
                type="button"
                data-testid="bp-agree"
                onClick={() => setAgree((a) => !a)}
                className="mt-2 flex items-center gap-2.5 min-h-[44px] text-left w-full"
              >
                {agree ? (
                  <CheckSquare className="w-5 h-5 shrink-0" style={{ color: BRAND }} />
                ) : (
                  <Square className="w-5 h-5 shrink-0 text-slate-400" />
                )}
                <span className="text-sm font-semibold text-slate-800">
                  I understand these points are non-refundable and expire on {opts.expiry_preview}.
                </span>
              </button>
            </div>
          </div>

          {selected && (
            <div className="rounded-2xl bg-blue-50 p-4" data-testid="bp-summary">
              <p className="text-[15px] text-slate-800">
                You pay <span className="font-black">${selected.pay_nzd.toFixed(2)}</span> →{" "}
                <span className="font-black">{selected.points.toLocaleString()} pts</span> ($
                {selected.face_nzd} value)
                {mode === "gift" && recipient ? ` for ${recipient.display_name}` : ""}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Valid until {opts.expiry_preview} · redeem at partner stores — can cover a whole
                bill, no review
              </p>
            </div>
          )}

          <button
            type="button"
            data-testid="bp-pay"
            onClick={pay}
            disabled={disabled}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl text-white font-black text-base min-h-[52px] disabled:opacity-50 transition-opacity"
            style={{ backgroundColor: BRAND }}
          >
            {busy === "pay" ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                {mode === "gift" ? "Pay & send gift" : "Pay with card"} · $
                {selected ? selected.pay_nzd.toFixed(2) : "—"}
              </>
            )}
          </button>
          <p className="text-center text-xs text-slate-500">
            Secure payment by Stripe · Apple Pay / Google Pay where available
          </p>

          {history.length > 0 && (
            <div>
              <div className="text-sm font-extrabold text-slate-900 mb-1 mt-2">
                Recent purchases &amp; gifts
              </div>
              <ul>
                {history.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center gap-3 py-2.5 border-b border-slate-100"
                    data-testid={`bp-hist-${p.id}`}
                  >
                    <span className="text-xl">
                      {p.direction === "received" ? "🎁" : p.direction === "sent" ? "📤" : "➕"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-800">
                        {p.direction === "received"
                          ? "Gift received"
                          : p.direction === "sent"
                          ? `Gift to ${p.recipient_display || "friend"}`
                          : "Bought for myself"}
                        {" · "}
                        {p.points.toLocaleString()} pts
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(p.created_at).toLocaleDateString()} · ${Number(p.total_nzd).toFixed(2)}
                        {p.status !== "paid"
                          ? ` · ${p.status}`
                          : p.points_expire_at
                          ? ` · valid until ${new Date(p.points_expire_at).toLocaleDateString()}`
                          : ""}
                      </div>
                      {/* Iter-627 — shareable gift-card PNG (public, unguessable id). */}
                      {(p.is_gift || p.direction === "sent") && p.status === "paid" && (
                        <button
                          type="button"
                          data-testid={`bp-share-card-${p.id}`}
                          onClick={async () => {
                            const base = SHARED_ORIGIN || window.location.origin;
                            const url = `${base}/api/points/purchase/${p.id}/gift-card.png`;
                            const message = `🎁 ${p.points.toLocaleString()} AllSale points gift`;
                            if (navigator.share) {
                              try {
                                await navigator.share({ title: message, text: message, url });
                                return;
                              } catch {
                                /* cancelled — fall through to copy */
                              }
                            }
                            try {
                              await navigator.clipboard.writeText(url);
                              toast.success("Gift card link copied — paste it anywhere");
                            } catch {
                              window.open(url, "_blank", "noopener");
                            }
                          }}
                          className="mt-1 text-xs font-extrabold"
                          style={{ color: BRAND }}
                        >
                          🖼️ Share gift card
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
